// pages/Products.js
import React, { useState, useEffect } from 'react';
import { 
  FaEdit, 
  FaTrash, 
  FaPlus, 
  FaBox, 
  FaRupeeSign, 
  FaEye, 
  FaEyeSlash,
  FaStar,
  FaImage,
  FaTags,
  FaCalendar,
  FaChartLine
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import SummaryApi from '../common';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const navigate = useNavigate();

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(SummaryApi.product.getAll.url, {
        method: SummaryApi.product.getAll.method,
        headers: {
          'Authorization': `Bearer ${SummaryApi.bearerToken}`
        }
      });

      const data = await response.json();
      
      if (data.success || data.status) {
        setProducts(data.data || []);
      } else {
        toast.error(data.message || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle delete product
  const handleDelete = async (productId) => {
    try {
      const formData = new FormData();
      formData.append('product_id', productId);
      
      const response = await fetch(SummaryApi.product.delete.url, {
        method: SummaryApi.product.delete.method,
        body: formData,
        headers: {
          'Authorization': `Bearer ${SummaryApi.bearerToken}`
        }
      });

      const data = await response.json();
      
      if (data.success || data.status) {
        toast.success(data.message || 'Product deleted successfully!');
        fetchProducts(); // Refresh the list
        setShowDeleteConfirm(null);
      } else {
        toast.error(data.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  // Toggle product status (active/inactive)
 const toggleStatus = async (productId, currentStatus) => {
  try {
    const newStatus = currentStatus === '1' ? '0' : '1';
    const formData = new FormData();
    formData.append('product_id', productId);
    formData.append('is_active', newStatus);

    const response = await fetch(SummaryApi.product.update.url, {
      method: SummaryApi.product.update.method,
      body: formData,
      headers: {
        'Authorization': `Bearer ${SummaryApi.bearerToken}`
      }
    });

    const data = await response.json();

    if (data.success || data.status) {
      toast.success('Product status updated!');
      fetchProducts(); // Refresh the list
    } else {
      toast.error(data.message || 'Failed to update status');
    }
  } catch (error) {
    console.error('Error updating status:', error);
    toast.error('Failed to update status');
  }
};


  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className="text-yellow-400" />);
    }
    
    if (hasHalfStar) {
      stars.push(<FaStar key="half" className="text-yellow-400 opacity-50" />);
    }
    
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaStar key={`empty-${i}`} className="text-gray-300" />);
    }
    
    return stars;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Product Catalog</h1>
          <p className="text-gray-600 mt-2">Manage your manufacturing products in an intuitive card view</p>
        </div>

        {/* Action Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                // UPDATED: Correct path for creating product
                onClick={() => navigate('/admin-panel/products/create')}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center transform hover:-translate-y-0.5"
              >
                <FaPlus className="mr-2" /> Add New Product
              </button>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="text-sm text-gray-600">
              Showing <span className="font-bold">{products.length}</span> products
              {selectedProduct && (
                <span className="ml-4">
                  Selected: <span className="font-bold">{selectedProduct.product_name}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
            <p className="text-gray-600 mt-4 text-lg">Loading products...</p>
          </div>
        )}

        {/* Products Grid View */}
        {!loading && viewMode === 'grid' && products.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => {
              // Construct URL
              const imageUrl = `${SummaryApi.productImage.url}${product.product_img1}`;
              
              return (
                <div 
                  key={product.product_id} 
                  className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
                >
                  {/* Product Image */}
                  <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200">
                    {product.product_img1 ? (
                      <img 
                        src={imageUrl} 
                        alt={product.product_name}
                        className="w-full h-full"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FaImage className="text-4xl text-gray-400" />
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className="absolute top-4 left-4">
                    <button
                      onClick={() => toggleStatus(product.product_id, product.is_active)}
                      className={`px-3 py-1 rounded-full text-xs font-bold transition-all duration-200 ${
                          product.is_active === '1' 
                          ? 'bg-green-500 text-white hover:bg-green-600' 
                          : 'bg-red-500 text-white hover:bg-red-600'
                      }`}
                      >
                      {product.is_active === '1' ? 'ACTIVE' : 'INACTIVE'}
                      </button>

                    </div>
                    
                    {/* Discount Badge */}
                    {product.discount > 0 && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full text-xs font-bold shadow-lg">
                          {product.discount}% OFF
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Product Content */}
                  <div className="p-5">
                    {/* Product Code & Date */}
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center text-sm text-gray-500">
                        <FaCalendar className="mr-1" />
                        <span>{formatDate(product.created_date)}</span>
                      </div>
                    </div>
                    
                    {/* Product Name */}
                    <h3 className="text-lg font-bold text-gray-800 mb-2 truncate" title={product.product_name}>
                      {product.product_name}
                    </h3>
                    
                    {/* Sub Title */}
                    {product.sub_title && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.sub_title}</p>
                    )}
                    
                    {/* Rating */}
                    <div className="flex items-center mb-4">
                      <div className="flex mr-2">
                        {renderStars(product.rating)}
                      </div>
                      <span className="text-sm text-gray-600">({product.rating || 0})</span>
                    </div>
                    
                    {/* Price Section */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-2xl font-bold text-blue-600">
                          ₹ {product.price}
                        </div>
                        {product.discount > 0 && (
                          <div className="text-sm text-gray-500">
                            <span className="line-through">₹ {product.after_discount_price}</span>
                            <span className="ml-2 text-green-600 font-medium">Save {product.discount}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Description Preview */}
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {product.product_description}
                      </p>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <button
                        // UPDATED: Correct path for editing in admin panel
                        onClick={() => navigate(`/admin-panel/products/${product.product_id}/edit`)}
                        className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium py-2.5 px-4 rounded-xl flex items-center justify-center transition-all duration-200"
                      >
                        <FaEdit className="mr-2" /> Edit
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Products List View */}
        {!loading && viewMode === 'list' && products.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {products.map((product) => {
                    // Construct URL
                    const imageUrl = `${SummaryApi.productImage.url}${product.product_img1}`;
                    
                    return (
                      <tr key={product.product_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 mr-4">
                              {product.product_img1 ? (
                                <img 
                                  src={imageUrl} 
                                  alt={product.product_name}
                                  className="h-full w-full object-cover"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/100?text=No+Img';
                                  }}
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                  <FaImage className="text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{product.product_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="flex items-center mb-1">
                              {renderStars(product.rating)}
                            </div>
                            <div className="text-gray-500 line-clamp-1">{product.sub_title || 'No subtitle'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-lg font-bold text-blue-600">₹ {product.price}</div>
                          {product.discount > 0 && (
                            <div className="text-sm text-gray-500">
                              <span className="line-through">₹ {product.after_discount_price}</span>
                              <span className="ml-1 text-green-600">{product.discount}% off</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => toggleStatus(product.product_id, product.is_active)}
                            className={`px-3 py-1 rounded-full text-xs font-bold ${
                              product.is_active === '1'
                                ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {product.is_active === '1' ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              // UPDATED: Correct path for editing in admin panel
                              onClick={() => navigate(`/admin-panel/products/${product.product_id}/edit`)}
                              className="text-blue-600 hover:text-blue-900 px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                            >
                              <FaEdit className="inline mr-1" /> Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && products.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-200 rounded-2xl mb-6">
              <FaBox className="text-3xl text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-3">No Products Found</h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">Start building your product catalog by adding your first product</p>
            <button
              // UPDATED: Correct path for creating product
              onClick={() => navigate('/admin-panel/products/create')}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center mx-auto transform hover:-translate-y-0.5"
            >
              <FaPlus className="mr-2" /> Add Your First Product
            </button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full transform transition-all duration-300 scale-100">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Confirm Delete</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this product? This action cannot be undone.</p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg"
                >
                  Delete Product
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;